import Banner from '@/components/common/Banner';
import { Button } from '@/components/common/Button';
import { FlexRow } from '@/components/common/Flex';
import InputField from '@/components/common/InputField';
import { linkHoverStyleProps } from '@/components/common/styleHelpers';
import { Paragraph } from '@/components/common/Typography';
import { useTracking } from '@/services/analytics/useTracking';
import { useLazyQuery } from '@apollo/client';
import { Text } from '@chakra-ui/react';
import {
    PromotionCodeDocument,
    PromotionCodeFieldsFragment,
} from '@common/graphql/generated';
import { useRef, useState } from 'react';

export const PromotionCodeRow = ({
    appliedPromotionCode,
    setAppliedPromotionCode,
}: {
    appliedPromotionCode: PromotionCodeFieldsFragment | null;
    setAppliedPromotionCode: (code: PromotionCodeFieldsFragment | null) => void;
}) => {
    const { track } = useTracking();
    const promotionCodeInputRef = useRef<HTMLInputElement>(null);
    const [isPromotionCodeInputVisible, setIsPromotionCodeInputVisible] =
        useState(false);
    const [invalidPromotionCode, setInvalidPromotionCode] = useState(false);
    const [promotionCode, setPromotionCode] = useState<string | null>(null);
    const [promotionCodeQuery] = useLazyQuery(PromotionCodeDocument, {
        fetchPolicy: 'no-cache',
    });

    const applyPromotionCode = async () => {
        if (!promotionCode) return;

        const { data } = await promotionCodeQuery({
            variables: {
                code: promotionCode,
            },
        });

        if (data?.promotionCode) {
            track('Promotion Code Applied', {
                code: promotionCode,
                amount_off: data.promotionCode.amountOff,
                percent_off: data.promotionCode.percentOff,
            });
            setAppliedPromotionCode(data.promotionCode);
        } else {
            track('Promotion Code Invalid', { code: promotionCode });
            setInvalidPromotionCode(true);
        }
    };

    if (appliedPromotionCode) {
        return (
            <Banner mt={6} backgroundColor="green.100" borderColor="green.100">
                Promotion code{' '}
                <Text fontWeight="bold" as="span" fontFamily="mono">
                    {promotionCode}
                </Text>{' '}
                applied successfully!
            </Banner>
        );
    }

    if (isPromotionCodeInputVisible) {
        return (
            <>
                <FlexRow
                    mt={6}
                    justify="space-between"
                    align="flex-end"
                    gap={10}
                >
                    <InputField
                        type="text"
                        name="code"
                        ref={promotionCodeInputRef}
                        placeholder="Add promotion code"
                        autoFocus
                        value={promotionCode}
                        onChange={(v) => {
                            setPromotionCode(v);
                            setInvalidPromotionCode(false);
                        }}
                        flex={4}
                        inputProps={{
                            size: 'sm',
                            variant: 'outline',
                        }}
                    />
                    <Button
                        maxW="85px"
                        h={10}
                        size="sm"
                        variant="outline"
                        disabled={!promotionCode}
                        onClick={applyPromotionCode}
                        onClickEvent="Apply Promotion Code Button Clicked"
                        colorScheme="black"
                        flex={1}
                    >
                        Apply
                    </Button>
                </FlexRow>
                {invalidPromotionCode && (
                    <Paragraph color="red.400" mt={2} fontSize="sm">
                        Sorry, we were unable to apply that promotion code
                    </Paragraph>
                )}
            </>
        );
    }

    return (
        <Text
            mt={6}
            fontSize="md"
            color="textMuted"
            {...linkHoverStyleProps()}
            onClick={() => {
                setIsPromotionCodeInputVisible(true);
                setTimeout(() => promotionCodeInputRef.current?.focus(), 50);
            }}
        >
            Add promotion code
        </Text>
    );
};
